import { Anchor as MantineAnchor, AnchorProps, useMantineTheme } from '@mantine/core';
import { forwardRef } from 'react';

export type GradientVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'success'
  | 'info'
  | 'warning'
  | 'danger';

export interface ThemedAnchorProps extends Omit<AnchorProps, 'gradient'> {
  gradient?: boolean;
  gradientVariant?: GradientVariant;
  underlineOnHover?: boolean;
  children?: React.ReactNode;
  /** Standard anchor href for direct links */
  href?: string;
  /** React Router Link component for internal navigation */
  component?: any;
  /** Route path when using component prop (e.g., with Link) */
  to?: string;
}

const Anchor = forwardRef<HTMLAnchorElement, ThemedAnchorProps>(
  ({ gradient = false, gradientVariant = 'primary', underlineOnHover = true, children, style, ...rest }, ref) => {
    const theme = useMantineTheme();

    if (!gradient) {
      return (
        <MantineAnchor ref={ref} style={style} {...rest}>
          {children}
        </MantineAnchor>
      );
    }

    const gradientConfig = theme.other.gradients[gradientVariant];

    return (
      <MantineAnchor
        ref={ref}
        underline="never"
        style={{
          backgroundImage: `linear-gradient(135deg, ${gradientConfig.from}, ${gradientConfig.to})`,
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          position: 'relative',
          transition: 'all 0.2s ease',
          ...style,
        }}
        {...rest}
      >
        {children}
      </MantineAnchor>
    );
  }
);

Anchor.displayName = 'Anchor';

export default Anchor;
